﻿import React, { useState, useEffect } from "react";
import { Box, IconButton, Dialog } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepTooltip,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import {
  selectCrossReferenceSummaryRecords,
  fetchCrossReferenceSummaries,
  selectCrossReferenceSummarySelectedTab,
  selectSelectedConnectionPair,
  selectCrossReferenceSummaryRecordsLoaded,
  clearTabCache,
  selectConnectionQualifiers,
  fetchItemQualifiers,
} from "@/features/maintenance/maintenanceSlice";
import AddCrossReferencePopup from "./AddCrossReferencePopup";
import CrossReferenceGridToolbar from "./CrossReferenceGridToolbar";
import EditPrioritiesPopup from "./EditPrioritiesPopup";
import "./Maintenance.css";
import DeleteIcon from "@/assets/img/icons/Delete/Line.svg?react";
import EditIcon from "@/assets/img/icons/Edit/Line.svg?react";
import {
  selectIsAddCrossRefOpen,
  setIsAddCrossRefOpen,
  setMapOnFlyInfo,
} from "@/features/connections/crossReferenceSlice";
import { createAlert } from "@/features/homepage/alertSliceList";
import { deleteCrossReferenceMapping } from "@/features/connections/crossReferenceSlice";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { CrossReferenceTypes } from "@/utils/utils";

const CrossReferenceGrid = (props) => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const connectionPair = useSelector(selectSelectedConnectionPair);
  const crossReferenceSummaryRecords = useSelector(
    selectCrossReferenceSummaryRecords
  );
  const selectedTab = useSelector(selectCrossReferenceSummarySelectedTab);
  const isAddCrossRefOpen = useSelector(selectIsAddCrossRefOpen);
  const connectionQualifiers = useSelector(selectConnectionQualifiers);
  const [referenceFilter, setReferenceFilter] = useState("");
  const recordLoaded = useSelector(selectCrossReferenceSummaryRecordsLoaded);
  const [selectedRow, setSelectedRow] = useState();
  const [editPrioritiesOpen, setEditPrioritiesOpen] = useState(false);

  useEffect(() => {
    if (Object.keys(connectionPair).length !== 0) {
      const selectedCrossReferenceInfo = {
        ConnectionPairId: connectionPair.ConnectionPairId,
        SourceConnection: connectionPair.ConnectionName1,
        SourceConnectionId: connectionPair.ConnectionId1,
        TargetConnection: connectionPair.ConnectionName2,
        TargetConnectionId: connectionPair.ConnectionId2,
        IdentifiedCatalogs: null,
        CrossReferenceTypes,
      };
      dispatch(
        setMapOnFlyInfo({
          crossReferenceInfo: selectedCrossReferenceInfo,
          crossReferenceWorkflowRecordId: null,
        })
      );

      dispatch(
        fetchItemQualifiers({
          connectionId: connectionPair.ConnectionId1
        })
      );
    }
  }, [connectionPair, dispatch]);

  const handleEditPriorities = (open, row) => {
    setSelectedRow(row)
    setEditPrioritiesOpen(open)
  }
  const handleDeleteRow = (row) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: "This Cross Reference will be deleted",
        message: "Are you sure you want to delete this record?",
        buttonLabel: "Yes",
        buttonLabel2: "No",
        onButtonClick: () => deleteCrossReference({ row }),
      })
    );
  };

  const deleteCrossReference = async ({ row = null }) => {
    const cascadingDelete = true;
    try {
      await dispatch(
        deleteCrossReferenceMapping({
          selectedMappings: [row.CrossReferenceMappingId],
          crossReferenceType: selectedTab, 
          connectionPairId: connectionPair.ConnectionPairId,
          cascadingDelete: cascadingDelete,
        })
      ).unwrap();
      await dispatch(
        createAlert({
          status: "Success",
          message: "Selected rows deleted successfully.",
        })
      );

      // SHEP-633 must refetch the cross references with the new priorities
      await dispatch(clearTabCache(selectedTab));
      await dispatch(
        fetchCrossReferenceSummaries({
          connectionPairId: connectionPair.ConnectionPairId,
          crossReferenceType: selectedTab,
        })
      );
      apiRef.current.updateRows([{ id: row.id, _action: "delete" }]);
    } catch (error) {
      console.error("Error deleting rows:", error);
      dispatch(
        createAlert({
          status: "Error",
          message: "An error occurred while deleting rows.",
        })
      );
    }
  };

  const qualifierColumns = connectionQualifiers.map((qualifier) => ({
    headerName: qualifier,
    field: `qualifier_${qualifier}`,
    headerSegment: 1,
    width: 200,
  }));

  // Update rows to include qualifier values
  const updatedRows = crossReferenceSummaryRecords.map((row) => {
    const qualifiers = JSON.parse(row.Qualifiers || "{}");
    const updatedRow = { ...row };
    Object.keys(qualifiers).forEach((key) => {
      updatedRow[`qualifier_${key}`] = qualifiers[key];
    });
    return updatedRow;
  });

  const columns = [
    [
      /*    Customer    */
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Related Account",
        field: "CustomerRelatedAccount",
        headerSegment: 1,
        width: 350,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 270,
      },
      {
        headerName: "ERP Customer Code",
        field: "ErpCustomerCode",
        headerSegment: 2,
        width: 250,
      },
    ],
    /*    Item    */
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Item Code",
        field: "CustomerItemCode",
        headerSegment: 1,
        width: 250,
      },
      ...qualifierColumns,
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 300,
      },
      {
        headerName: "ERP Item Code",
        field: "ErpItemCode",
        headerSegment: 2,
        width: 200,
      },
    ],
    /*    Item UOM    */
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Item Code",
        field: "CustomerItemCode",
        headerSegment: 1,
        width: 250,
      },
      {
        headerName: "Customer Item UOM",
        field: "CustomerItemUOM",
        headerSegment: 1,
        width: 250,
      },
      {
        headerName: "Customer UOM Factor",
        field: "CustomerUOMFactor",
        headerSegment: 1,
        width: 265,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 300,
      },
      {
        headerName: "ERP Item Code",
        field: "ErpItemCode",
        headerSegment: 2,
        width: 200,
      },
      {
        headerName: "ERP Base UOM",
        field: "ErpBaseUOM",
        headerSegment: 2,
        width: 200,
      },
      {
        headerName: "ERP UOM Factor",
        field: "ErpUOMFactor",
        headerSegment: 2,
        width: 210,
      },
    ],
    /*    Warehouse    */
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Warehouse Code",
        field: "CustomerWarehouseCode",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 300,
      },
      {
        headerName: "ERP Warehouse Code",
        field: "ErpWarehouseCode",
        headerSegment: 2,
        width: 330,
      },
    ],
    /*    Ship To    */
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Ship To Code",
        field: "CustomerShipToCode",
        headerSegment: 1,
        width: 300,
      },
      {
        headerName: "Customer Related Account",
        field: "CustomerRelatedAccount",
        headerSegment: 1,
        width: 315,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 280,
      },
      {
        headerName: "ERP Customer Code",
        field: "ErpCustomerCode",
        headerSegment: 2,
        width: 280,
      },
      {
        headerName: "ERP Ship To Code",
        field: "ErpShipToCode",
        headerSegment: 2,
        width: 250,
      },
    ],
    /*    Carrier    */
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 320,
      },
      {
        headerName: "Customer Carrier Code",
        field: "CustomerCarrierCode",
        headerSegment: 1,
        width: 270,
      },
      {
        headerName: "SCAC",
        field: "CustomerScac",
        width: 100,
        headerSegment: 1,
      },
      {
        headerName: "Routing",
        field: "CustomerRouting",
        headerSegment: 1,
        width: 125,
      },
      {
        headerName: "Service Level",
        field: "CustomerServiceLevel",
        headerSegment: 1,
        width: 175,
      },
      {
        headerName: "Transportation Type",
        field: "CustomerTransportationMethodType",
        headerSegment: 1,
        width: 245,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 270,
      },
      {
        headerName: "ERP Carrier Code",
        field: "ErpCarrierCode",
        headerSegment: 2,
        width: 215,
      },
    ],
    /*    Allowance    */
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Discount / Charge Code",
        field: "CustomerDiscountChargeCode",
        headerSegment: 1,
        width: 380,
      },
      {
        headerName: "Customer D/C Type",
        field: "CustomerDiscountChargeType",
        headerSegment: 1,
        width: 250,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 280,
      },
      {
        headerName: "ERP Discount / Charge Code",
        field: "ErpDiscountChargeCode",
        headerSegment: 2,
        width: 350,
      },
    ],
    // Tax Groups
    [
      {
        headerName: "Customer Connection Name",
        field: "CustomerConnectionName",
        headerSegment: 1,
        width: 330,
      },
      {
        headerName: "Customer Tax Group Code",
        field: "CustomerTaxGroupCode",
        headerSegment: 1,
        width: 380,
      },
      {
        headerName: "ERP Connection Name",
        field: "ErpConnectionName",
        headerSegment: 2,
        width: 280,
      },
      {
        headerName: "ERP Tax Group Code",
        field: "ErpTaxGroupCode",
        headerSegment: 2,
        width: 350,
      },
    ],
  ];

  const sharedColumns = [
    {
      field: "Priority",
      headerName: "Priority",
      width: 150,
      headerSegment: 3,
    },
    {
      field: "ConnectionPairId",
      headerName: "Connection Pair",
      headerSegment: 3,
    },
    {
      field: "CrossReferenceType",
      headerName: "Catalog Type",
      headerSegment: 3,
    },
    {
      headerName: "Cross Reference Mapping Id",
      field: "CrossReferenceMappingId",
      headerSegment: 3,
    },
    {
      field: "actions",
      headerName: "actions",
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEditPriorities(true, params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteRow(params.row)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
      sortable: false,
      filterable: false,
      resizable: false,
    },
  ];

  return (
    <Box sx={{ display: "block", flexDirection: "column", width: "100%" }}>
      {
        editPrioritiesOpen && (
          <Dialog
            className="add-crossreference-container"
            open={editPrioritiesOpen}
            onClose={() => handleEditPriorities(false, null)}
            maxWidth="xl"
            PaperProps={{
              style: {
                backgroundColor: "transparent",
                boxShadow: "none",
                width: "2000px",
                height: "1600px",
              },
            }}
            sx={{ zIndex: "1500" }}
          >
            <EditPrioritiesPopup 
              onClose={() => handleEditPriorities(false, null)} 
              crossReferenceMappingId={selectedRow.CrossReferenceMappingId}
            />
          </Dialog>
        )
      }
      {isAddCrossRefOpen && (
        <Dialog
          className="add-crossreference-container"
          open={isAddCrossRefOpen}
          onClose={() => dispatch(setIsAddCrossRefOpen({ value: false }))}
          maxWidth="xl"
          PaperProps={{
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
              width: "2000px",
              height: "1600px",
            },
          }}
          sx={{ zIndex: "1500" }}
        >
          <AddCrossReferencePopup />
        </Dialog>
      )}
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              height: "100%",
            }}
          >
            <ShepDataGridPro
              apiRef={apiRef}
              isDataLoaded={recordLoaded}
              rows={updatedRows}
              columns={columns[selectedTab].concat(sharedColumns)}
              columnHeaderHeight={78}
              rowHeight={100}
              pagination
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15, page: 0 },
                },
                pinnedColumns: {
                  right: ["actions"],
                },
              }}
              whiteHeader={true}
              checkboxSelection={true}
              disableMultipleRowSelection={false}
              disableColumnMenu={true}
              disableColumnFilter={true}
              disableColumnReorder={true}
              hideFooterSelectedRowCount={true}
              columnVisibilityModel={{
                CrossReferenceType: false,
                ConnectionPairId: false,
                CrossReferenceMappingId: false,
              }}
              disableRowSelectionOnClick={!props.canSelect}
              slots={{
                baseCheckbox: ShepCheckbox,
                baseTooltip: ShepTooltip,
                pagination: ShepPagination,
                toolbar: CrossReferenceGridToolbar,
              }}
              sx={{
                color: "var(--text-primary)",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "44px",
                borderTopRightRadius: "44px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid:hover": {
                  backgroundColor: "inherit",
                },
                "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                  opacity: 0,
                  borderBottom: "none",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                {
                  outline: "none",
                },
                "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                {
                  opacity: 1,
                },
                "& .MuiDataGrid-cellCheckbox": {
                  borderLeft: "8px solid transparent",
                },
                "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                  opacity: 1,
                  borderLeft: "8px solid var(--primary-main)",
                  borderTopLeftRadius: "4px",
                  borderBottomLeftRadius: "4px",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                {
                  borderBottomLeftRadius: "4px",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "var(--primary-pale)",
                  borderBottom: "none",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-pinnedColumnHeaders.MuiDataGrid-pinnedColumnHeaders--right":
                {
                  display: "none",
                },
                "& .MuiDataGrid-columnHeader[data-field='ErpConnectionName']": {
                  paddingLeft: "45px",
                },
                "& .MuiDataGrid-cell[data-field='ErpConnectionName']": {
                  paddingLeft: "45px",
                },
                ...(Object.keys(connectionPair).length === 0
                  ? {
                    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-footerContainer, & .MuiDataGrid-scrollbar, & .MuiDataGrid-virtualScroller, & .gridHeaderSegment":
                      { display: "none" },
                    background: "transparent",
                    minHeight: "600px",
                  }
                  : {}),
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CrossReferenceGrid;
